// Bundle State Manager (with Price Tracking and Discounts)
class BundleState {
  constructor(size, bundleType = 'percentage', bundleValues = null) {
    this.size = size;
    this.products = Array(size).fill(null);
    this.variantSet = new Set();
    this.totalPrice = 0; // Track total price
    this.bundleType = bundleType; // 'percentage' or 'fixed_price'
    this.bundleValues = bundleValues; // String in format "count|value,count|value" or array

    this.initDiscounts();
  }

  initDiscounts() {
    // Parse bundle values if provided as count|value pairs
    if (this.bundleValues && typeof this.bundleValues === 'string') {
      this.bundleValueMap = this.parseBundleValues(this.bundleValues);
    } else if (Array.isArray(this.bundleValues)) {
      // Convert array format to map (legacy support)
      this.bundleValueMap = {};
      this.bundleValues.forEach((value, index) => {
        this.bundleValueMap[index + 3] = value; // Start from 3 items
      });
    } else {
      // Set default values based on bundle type
      this.bundleValueMap = {};
      if (this.bundleType === 'percentage') {
        // Default percentage discounts
        this.bundleValueMap = {3: 10, 4: 15, 5: 20, 6: 25, 7: 30};
      } else if (this.bundleType === 'fixed_price') {
        // Default fixed prices
        this.bundleValueMap = {3: 1000, 4: 1800, 5: 2500, 6: 3200, 7: 4000};
      }
    }
    
    console.log('Bundle initialized:', {
      type: this.bundleType,
      valueMap: this.bundleValueMap,
      size: this.size
    });
  }

  // Parse bundle values in format "2|0,3|5,4|7,5|10,6|15,7|30"
  parseBundleValues(bundleValueString) {
    const valueMap = {};
    
    try {
      const pairs = bundleValueString.split(',');
      pairs.forEach(pair => {
        const [count, value] = pair.trim().split('|');
        if (count && value !== undefined) {
          valueMap[parseInt(count)] = parseFloat(value);
        }
      });
    } catch (error) {
      console.error('Error parsing bundle values:', error);
    }
    
    return valueMap;
  }

  // Get bundle value (percentage or fixed price) based on product count
  getBundleValue(productCount) {
    // Only return value if explicitly defined for this count
    return this.bundleValueMap[productCount] || 0;
  }

  // Check if a discount/fixed price is explicitly defined for this count
  hasDefinedValueForCount(productCount) {
    return this.bundleValueMap.hasOwnProperty(productCount) && this.bundleValueMap[productCount] > 0;
  }

  addProduct(index, product) {
    if (this.isFull()) return false;
    if (this.variantSet.has(product.variantId)) return false;
    
    this.products[index] = product;
    this.variantSet.add(product.variantId);
    
    // Add product price to total
    this.totalPrice += product.price;
    
    // Dispatch state change event
    this.dispatchStateChange();
    return true;
  }

  removeProduct(index) {
    if (!this.products[index]) return null;
    
    const removed = this.products[index];
    this.variantSet.delete(removed.variantId);
    
    // Subtract removed product price
    this.totalPrice -= removed.price;
    
    // Shift products forward
    for (let i = index; i < this.size - 1; i++) {
      this.products[i] = this.products[i + 1];
    }
    this.products[this.size - 1] = null;
    
    // Dispatch state change event
    this.dispatchStateChange();
    return removed;
  }

  getNextAvailableIndex() {
    return this.products.findIndex(item => item === null);
  }

  isFull() {
    return this.products.every(item => item !== null);
  }

  containsVariant(variantId) {
    return this.variantSet.has(variantId);
  }

  // Get current product count
  getProductCount() {
    return this.products.filter(item => item !== null).length;
  }

  // Get raw total without any discounts
  getTotal() {
    return this.totalPrice;
  }

  // Calculate final price based on bundle type
  calculatePricing() {
    const productCount = this.getProductCount();
    
    // Only apply discount if explicitly defined for this count and value > 0
    const bundleValue = this.getBundleValue(productCount);
    const hasDefinedDiscount = this.hasDefinedValueForCount(productCount);
    
    if (!hasDefinedDiscount) {
      return {
        originalTotal: this.totalPrice,
        finalPrice: this.totalPrice,
        discountAmount: 0,
        discountPercentage: 0,
        hasDiscount: false
      };
    }

    let finalPrice = this.totalPrice;
    let discountAmount = 0;
    let discountPercentage = 0;

    if (this.bundleType === 'percentage') {
      // Percentage-based discounts
      discountPercentage = bundleValue;
      discountAmount = (this.totalPrice * discountPercentage) / 100;
      finalPrice = this.totalPrice - discountAmount;
    } else if (this.bundleType === 'fixed_price') {
      // Fixed price bundles
      finalPrice = bundleValue;
      discountAmount = Math.max(0, this.totalPrice - bundleValue);
      discountPercentage = this.totalPrice > 0 ? Math.round((discountAmount / this.totalPrice) * 100) : 0;
    }

    return {
      originalTotal: this.totalPrice,
      finalPrice: finalPrice,
      discountAmount: discountAmount,
      discountPercentage: discountPercentage,
      hasDiscount: discountAmount > 0
    };
  }

  // Get discount percentage based on product count
  getDiscountPercentage() {
    const pricing = this.calculatePricing();
    return pricing.discountPercentage;
  }

  // Get discount amount
  getDiscountAmount() {
    const pricing = this.calculatePricing();
    return pricing.discountAmount;
  }

  // Get final price after discount
  getFinalPrice() {
    const pricing = this.calculatePricing();
    return pricing.finalPrice;
  }

  // Check if discount is applicable
  hasDiscount() {
    const pricing = this.calculatePricing();
    return pricing.hasDiscount;
  }

  // Format price as currency
  getFormattedTotal() {
    return this.formatCurrency(this.totalPrice);
  }

  // Format final price (after discount) as currency
  getFormattedFinalPrice() {
    const pricing = this.calculatePricing();
    return this.formatCurrency(pricing.finalPrice);
  }

  // Format discount amount as currency
  getFormattedDiscountAmount() {
    const pricing = this.calculatePricing();
    return this.formatCurrency(pricing.discountAmount);
  }
  
  // Simple currency formatter (extend for multi-currency)
  formatCurrency(price) {
    return `Rs. ${price.toFixed(0)}`;
  }

  // Dispatch state change event with detailed pricing info
  dispatchStateChange() {
    const pricing = this.calculatePricing();
    const detail = {
      products: this.products,
      productCount: this.getProductCount(),
      bundleType: this.bundleType,
      originalTotal: pricing.originalTotal,
      finalPrice: pricing.finalPrice,
      discountAmount: pricing.discountAmount,
      discountPercentage: pricing.discountPercentage,
      hasDiscount: pricing.hasDiscount,
      formattedTotal: this.getFormattedTotal(),
      formattedFinalPrice: this.getFormattedFinalPrice(),
      formattedDiscountAmount: this.getFormattedDiscountAmount(),
      // Legacy compatibility
      state: this.products,
      isFull: this.isFull(),
      totalPrice: this.totalPrice
    };

    document.dispatchEvent(new CustomEvent('bundle:statechange', { detail }));
  }

  // Update bundle configuration (useful for dynamic changes)
  updateBundleConfig(bundleType, bundleValues) {
    this.bundleType = bundleType;
    this.bundleValues = bundleValues;
    this.initDiscounts();
    this.dispatchStateChange();
  }
}

// Global Bundle State
let bundleState = null;

// Initialize Bundle State based on component data
function initializeBundleState() {
  const bundleComponent = document.querySelector('bundle-feature') || 
                          document.querySelector('sticky-bundle-add-to-cart');
  if (!bundleComponent) {
    console.warn('Bundle component not found');
    return;
  }

  const bundleCount = parseInt(bundleComponent.getAttribute('data-steps') || 
                              bundleComponent.getAttribute('data-count')) || 7;
  const bundleType = bundleComponent.getAttribute('data-bundle-type') || 'percentage';
  const bundleValueString = bundleComponent.getAttribute('data-bundle-value');
  
  console.log('Initializing with raw bundle value:', bundleValueString);

  // Create global bundle state with string value (will be parsed in initDiscounts)
  window.bundleState = bundleState = new BundleState(bundleCount, bundleType, bundleValueString);
  
  console.log('Bundle state initialized:', {
    size: bundleCount,
    type: bundleType,
    rawValue: bundleValueString,
    parsedMap: bundleState.bundleValueMap
  });

  // Initial state dispatch
  bundleState.dispatchStateChange();
}

// Handle bundle product addition (call this from product selection)
function addProductToBundle(product) {
  if (!bundleState) {
    initializeBundleState();
  }
  
  const index = bundleState.getNextAvailableIndex();
  if (index !== -1) {
    return bundleState.addProduct(index, product);
  }
  return false;
}

// Export for global access
window.addProductToBundle = addProductToBundle;
window.BundleState = BundleState;
window.initializeBundleState = initializeBundleState;

// Bundle Feature Container
if (!customElements.get('bundle-feature')) {
  class BundleFeature extends HTMLElement {
    constructor() {
      super();
      this.boundHandleAdd = this.handleAddProduct.bind(this);
      this.boundHandleRemove = this.handleRemoveProduct.bind(this);
      this.initState();
      this.initEventListeners();
    }

    initState() {
      // Get configuration from attributes
      const bundleSize = parseInt(this.getAttribute('data-steps')) || 7;
      const bundleType = this.getAttribute('data-bundle-type') || 'percentage';
      const bundleValue = this.getAttribute('data-bundle-value');
      
      // Initialize global bundle state
      if (!window.bundleState) {
        window.bundleState = bundleState = new BundleState(bundleSize, bundleType, bundleValue);
      }
      
      this.dispatchStateChange();
    }

    initEventListeners() {
      document.addEventListener('bundle:add', this.boundHandleAdd);
      document.addEventListener('bundle:remove', this.boundHandleRemove);
    }

    disconnectedCallback() {
      document.removeEventListener('bundle:add', this.boundHandleAdd);
      document.removeEventListener('bundle:remove', this.boundHandleRemove);
    }

    handleAddProduct(event) {
      const { productData, stepIndex } = event.detail;
      if (bundleState.addProduct(stepIndex, productData)) {
        this.updateStepUI(stepIndex, productData);
        // State change is automatically dispatched by BundleState
      }
    }

    handleRemoveProduct(event) {
      const stepIndex = event.detail;
      const removedProduct = bundleState.removeProduct(stepIndex);
      if (removedProduct) {
        this.updateAllStepsUI();
        // State change is automatically dispatched by BundleState
      }
    }

    updateAllStepsUI() {
      for (let i = 0; i < bundleState.size; i++) {
        const product = bundleState.products[i];
        const stepEl = this.querySelector(`[data-index="${i}"]`);
        if (!stepEl) continue;
        
        if (product) {
          this.updateStepUI(i, product);
        } else {
          this.resetStepUI(i);
        }
      }
    }

    updateStepUI(index, product) {
      const stepEl = this.querySelector(`[data-index="${index}"]`);
      if (!stepEl) return;
      
      stepEl.innerHTML = `
        <div class="list-product has-product list-product--${index}">
          <div class="list-image--container" style="background-color: ${product.color}">
            <img src="${product.image}" alt="${product.title}" loading="lazy">
            <button class="remove-btn" data-index="${index}">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M10.5 1.5L1.5 10.5M1.5 1.5L10.5 10.5" stroke="#F7F7F7" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
          <div class="item--index step-label">item ${index + 1}</div>
          <span class="bundle-product--title">${product.title}</span>
        </div>
      `;
      
      stepEl.classList.add("has-product");
      
      stepEl.querySelector('.remove-btn').addEventListener('click', (e) => {
        e.stopPropagation();
        document.dispatchEvent(new CustomEvent('bundle:remove', {
          detail: index
        }));
      });
    }

    resetStepUI(index) {
      const stepEl = this.querySelector(`[data-index="${index}"]`);
      if (!stepEl) return;
      
      stepEl.innerHTML = `
        <div class="steps-list__step-number">${index + 1}</div>  
        <div class="steps-list__step-label steps-list__step-label--title">
            Select Your
            <span>Item  ${index + 1}</span>
        </div>
      `;
      stepEl.classList.remove("has-product");
    }

    // Legacy method for compatibility
    dispatchStateChange() {
      if (bundleState) {
        bundleState.dispatchStateChange();
      }
    }
  }
  customElements.define('bundle-feature', BundleFeature);
}

// Bundle Button Element
if (!customElements.get('bundle-button')) {
  class BundleButton extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.handleStateChange = this.handleStateChange.bind(this);
      this.addEventListener('click', this.handleClick);
      document.addEventListener('bundle:statechange', this.handleStateChange);
      
      // Store original button text
      this.originalText = this.textContent.trim();
      this.addedText = 'Added to Box';
      this.defaultText = 'Add to Box';
      
      this.updateDisabledState();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      document.removeEventListener('bundle:statechange', this.handleStateChange);
    }

    handleClick() {
      if (this.disabled) return;
      
      // Ensure bundle state exists
      if (!bundleState) {
        if (window.initializeBundleState) {
          window.initializeBundleState();
        } else {
          console.error('Bundle state not initialized');
          return;
        }
      }
      
      const nextIndex = bundleState.getNextAvailableIndex();
      if (nextIndex === -1) return;

      this.dispatchAddEvent(nextIndex);
    }

    dispatchAddEvent(stepIndex) {
      document.dispatchEvent(new CustomEvent('bundle:add', {
        detail: {
          stepIndex,
          productData: {
            title: this.dataset.productTitle,
            image: this.dataset.productImage,
            variantId: this.dataset.variantId,
            color: this.dataset.color || '#ccc',
            price: parseInt(this.dataset.productPrice) || 0 // Capture product price
          }
        }
      }));
    }

    handleStateChange() {
      this.updateDisabledState();
      this.updateButtonText();
    }

    updateDisabledState() {
      if (!bundleState) return;
      
      const variantAdded = bundleState.containsVariant(this.dataset.variantId);
      const bundleFull = bundleState.isFull();
      
      this.disabled = variantAdded || bundleFull;
      this.classList.toggle('disabled', this.disabled);
      this.setAttribute('aria-disabled', this.disabled);
    }

    updateButtonText() {
      if (!bundleState) return;
      
      const variantAdded = bundleState.containsVariant(this.dataset.variantId);
      const bundleFull = bundleState.isFull();
      
      if (variantAdded) {
        this.textContent = this.addedText;
      } else if (bundleFull) {
        this.textContent = this.defaultText;
      } else {
        this.textContent = this.defaultText;
      }
    }
  }
  customElements.define('bundle-button', BundleButton);
}

// Sticky Bundle Component (with Price and Footer Observer)
if (!customElements.get('sticky-bundle-add-to-cart')) {
  class StickyBundleAddToCart extends HTMLElement {
    constructor() {
      super();
      this.handleStateChange = this.handleStateChange.bind(this);
      this.handleRemove = this.handleRemove.bind(this);
      this.handleAddToCart = this.handleAddToCart.bind(this);
      this.handleFooterVisibility = this.handleFooterVisibility.bind(this);
      
      this.addToCartBtn = this.querySelector('.bundle-add-to-cart__right-btn');
      this.priceElement = this.querySelector('.bundle-add-to-cart__right-price span');
      this.discountedPriceElement = this.querySelector('.discounted-bundle--price');
      this.discountedPriceSpan = this.querySelector('.discounted-bundle--price s');
      this.discountPercentageElement = this.querySelector('.discount-percentage');
      this.messageElement = this.querySelector('.bundle-add-to-cart__message');

      this.addToCartBtn.addEventListener('click', this.handleAddToCart);
      
      document.addEventListener('bundle:statechange', this.handleStateChange);
      this.addEventListener('click', this.handleRemove);
      
      // Initialize Intersection Observer for footer
      this.observer = null;
      this.initFooterObserver();
    }

    connectedCallback() {
      // Initialize bundle state when component is connected
      if (!window.bundleState && window.initializeBundleState) {
        window.initializeBundleState();
      }
      this.handleStateChange();
    }

    disconnectedCallback() {
      document.removeEventListener('bundle:statechange', this.handleStateChange);
      this.removeEventListener('click', this.handleRemove);
      this.addToCartBtn.removeEventListener('click', this.handleAddToCart);
      
      if (this.observer) {
        this.observer.disconnect();
      }
    }

    initFooterObserver() {
      const footer = document.querySelector('footer') || 
                    document.querySelector('.footer') || 
                    document.querySelector('#footer');
      
      if (!footer) {
        console.warn('Footer element not found for sticky bundle observer');
        return;
      }

      this.observer = new IntersectionObserver(this.handleFooterVisibility, {
        root: null,
        rootMargin: '0px',
        threshold: 0.1
      });

      this.observer.observe(footer);
    }

    handleFooterVisibility(entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.style.transform = 'translateY(100%)';
          this.style.opacity = '0';
          this.style.visibility = 'hidden';
        } else {
          this.style.transform = 'translateY(0)';
          this.style.opacity = '1';
          this.style.visibility = 'visible';
        }
      });
    }

    handleStateChange(event) {
      if (!bundleState) return;

      // Get state detail from event or generate from bundleState directly
      const stateDetail = event?.detail || {
        productCount: bundleState.getProductCount(),
        bundleType: bundleState.bundleType,
        hasDiscount: bundleState.hasDiscount(),
        discountPercentage: bundleState.getDiscountPercentage(),
        formattedTotal: bundleState.getFormattedTotal(),
        formattedFinalPrice: bundleState.getFormattedFinalPrice(),
        formattedDiscountAmount: bundleState.getFormattedDiscountAmount()
      };

      // Update remaining count text
      const remainingCount = bundleState.size - stateDetail.productCount;
      const currentCountEl = this.querySelector('.current-count');
      const remainingTextEl = this.querySelector('.remaining-count-text');

      if (currentCountEl) {
        currentCountEl.textContent = remainingCount;
      }

      // Hide/show remaining count text based on whether bundle is complete
      if (remainingTextEl) {
        if (remainingCount === 0) {
          remainingTextEl.classList.add('hidden');
        } else {
          remainingTextEl.classList.remove('hidden');
        }
      }

      // Update step indicators
      bundleState.products.forEach((product, index) => {
        const stepEl = this.querySelector(`[data-index="${index}"]`);
        if (!stepEl) return;
        
        if (product) {
            stepEl.innerHTML = `
              <div class="list-product isolate list-product--${index}">
                <div class="list-image--container" style="background-color: ${product.color}">
                  <img src="${product.image}" alt="${product.title}" loading="lazy">
                  <button class="remove-btn" data-index="${index}">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M10.5 1.5L1.5 10.5M1.5 1.5L10.5 10.5" stroke="#F7F7F7" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>
            `;
            stepEl.classList.add("has-product")
        } else {
          stepEl.innerHTML = `<div class="steps-list__step-number">${index + 1}</div>`;
          stepEl.classList.remove("has-product")
        }
      });
      
      // Update message based on bundle state
      this.updateMessage(stateDetail);
      
      // Update price display
      if (this.priceElement && stateDetail) {
        if (stateDetail.hasDiscount) {
          this.priceElement.textContent = stateDetail.formattedFinalPrice;
          
          if (this.discountedPriceSpan) {
            this.discountedPriceSpan.textContent = stateDetail.formattedTotal;
          }
          
          if (this.discountedPriceElement) {
            this.discountedPriceElement.classList.remove('hidden');
          }
          
          if (this.discountPercentageElement) {
            if (stateDetail.bundleType === 'percentage') {
              this.discountPercentageElement.textContent = `(${stateDetail.discountPercentage}% OFF)`;
            } else {
              this.discountPercentageElement.textContent = `(Save ${stateDetail.formattedDiscountAmount})`;
            }
            this.discountPercentageElement.classList.remove('hidden');
          }
        } else {
          this.priceElement.textContent = stateDetail.formattedTotal;
          
          if (this.discountedPriceElement) {
            this.discountedPriceElement.classList.add('hidden');
          }
          
          if (this.discountPercentageElement) {
            this.discountPercentageElement.classList.add('hidden');
          }
        }
      }
      
      // Update button state - only enable when bundle is complete (matches bundle size)
      const isBundleComplete = bundleState.isFull();
      this.addToCartBtn.disabled = !isBundleComplete;
      this.addToCartBtn.classList.toggle('disabled', !isBundleComplete);

      this.addToCartBtn.style.visibility = 'visible';
      this.addToCartBtn.style.opacity = isBundleComplete ? '1' : '0.6';
      this.addToCartBtn.style.display = 'block';
    }

    updateMessage(stateDetail) {
      if (!this.messageElement || !stateDetail) return;
      
      const productCount = stateDetail.productCount;
      let message = '';
      
      // Get next milestone (next count that has a defined discount/price)
      const getNextMilestone = (currentCount) => {
        // Get all defined counts greater than current with value > 0
        const definedCounts = Object.keys(bundleState.bundleValueMap)
          .map(count => parseInt(count))
          .filter(count => count > currentCount && bundleState.bundleValueMap[count] > 0)
          .sort((a, b) => a - b);
        
        if (definedCounts.length > 0) {
          const nextCount = definedCounts[0];
          return { count: nextCount, value: bundleState.bundleValueMap[nextCount] };
        }
        return null;
      };
      
      // Check if current count has a defined discount/price
      const hasCurrentDiscount = bundleState.hasDefinedValueForCount(productCount);
      
      if (stateDetail.bundleType === 'percentage') {
        // Percentage-based messages
        if (productCount === 0) {
          const nextMilestone = getNextMilestone(0);
          if (nextMilestone) {
            message = `Add ${nextMilestone.count} products & get ${nextMilestone.value}% Off.`;
          } else {
            message = `Start building your bundle!`;
          }
        } else {
          const nextMilestone = getNextMilestone(productCount);
          if (nextMilestone) {
            const itemsNeeded = nextMilestone.count - productCount;
            message = `Add ${itemsNeeded} more product${itemsNeeded > 1 ? 's' : ''} & get ${nextMilestone.value}% Off.`;
          } else {
            if (hasCurrentDiscount && stateDetail.hasDiscount) {
              message = `You're getting ${stateDetail.discountPercentage}% off your ${productCount}-item bundle!`;
            } else {
              message = `${productCount} items selected - regular pricing applies.`;
            }
          }
        }
      } else if (stateDetail.bundleType === 'fixed_price') {
        // Fixed price-based messages
        if (productCount === 0) {
          const nextMilestone = getNextMilestone(0);
          if (nextMilestone) {
            message = `Add ${nextMilestone.count} products & pay only Rs. ${nextMilestone.value}.`;
          } else {
            message = `Start building your bundle!`;
          }
        } else {
          const nextMilestone = getNextMilestone(productCount);
          if (nextMilestone) {
            const itemsNeeded = nextMilestone.count - productCount;
            message = `Add ${itemsNeeded} more product${itemsNeeded > 1 ? 's' : ''} & pay only Rs. ${nextMilestone.value}.`;
          } else {
            if (hasCurrentDiscount && stateDetail.hasDiscount) {
              message = `Fixed bundle price: ${stateDetail.formattedFinalPrice} (Save ${stateDetail.formattedDiscountAmount}!)`;
            } else {
              message = `${productCount} items selected - regular pricing applies.`;
            }
          }
        }
      }
      
      this.messageElement.textContent = message;
    }

    handleRemove(event) {
      const removeBtn = event.target.closest('.remove-btn');
      if (!removeBtn) return;
      
      const index = parseInt(removeBtn.dataset.index);
      document.dispatchEvent(new CustomEvent('bundle:remove', {
        detail: index
      }));
    }

    async handleAddToCart() {
      if (!bundleState.isFull()) return;

      const pricingDetails = bundleState.calculatePricing();

      console.log('Adding bundle to cart:', bundleState.products);
      console.log('Bundle pricing details:', pricingDetails);

      try {
        const bundleId = new Date().getTime();

        // Get bundle info from data attributes
        const parentVariantId = this.getAttribute('data-parent-variant-id') || '';
        const parentProductId = this.getAttribute('data-parent-product-id') || '';
        const bundleTitle = this.getAttribute('data-bundle-title') || '';
        const bundleImage = this.getAttribute('data-bundle-image') || '';
        const minimumQuantity = this.getAttribute('data-minimum-quantity') || this.getAttribute('data-count') || '2';

        const itemsWithBundleId = bundleState.products
          .filter(variant => variant !== null)
          .map(variant => ({
            id: variant.variantId,
            quantity: 1,
            properties: {
              bundleInfo: `${bundleId}|${parentVariantId}|${parentProductId}|${bundleTitle}|${bundleImage}|${minimumQuantity}`,
              bundleType: bundleState.bundleType,
              bundleDiscount: bundleState.bundleType === 'percentage' 
                ? `${pricingDetails.discountPercentage}%`
                : bundleState.formatCurrency(pricingDetails.discountAmount),
              bundleOriginalPrice: bundleState.getFormattedTotal(),
              bundleFinalPrice: bundleState.getFormattedFinalPrice(),
              bundleItemCount: bundleState.getProductCount(),
              bundleDiscountAmount: bundleState.getFormattedDiscountAmount()
            }
          }));

        // Check if new-cart-drawer exists before trying to use it
        const cartDrawer = document.querySelector("new-cart-drawer");
        if (cartDrawer && typeof cartDrawer.updateCart === 'function') {
          cartDrawer.updateCart(null, 1, itemsWithBundleId, false, null);
          cartDrawer.classList.add('open');
        } else {
          // Fallback to Shopify's cart API if new-cart-drawer doesn't exist
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ items: itemsWithBundleId })
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const result = await response.json();
          console.log('Cart updated successfully:', result);
          
          // Trigger cart refresh or redirect
          window.location.href = '/cart';
        }

        // Show success message
        this.showSuccessMessage();

      } catch (error) {
        console.error('Cart error:', error);
        this.showErrorMessage(error.message || 'Failed to add products to cart. Please try again.');
      }
    }

    showSuccessMessage() {
      // Show a temporary success message
      const originalText = this.addToCartBtn.textContent;
      this.addToCartBtn.textContent = 'Added to Cart!';
      this.addToCartBtn.classList.add('success');
      
      setTimeout(() => {
        this.addToCartBtn.textContent = originalText;
        this.addToCartBtn.classList.remove('success');
      }, 2000);
    }

    showErrorMessage(message) {
      // Show error message (customize based on your site's error handling)
      if (typeof showError === 'function') {
        showError(message);
      } else if (window.alert) {
        alert(message);
      } else {
        console.error(message);
      }
    }
  }
  customElements.define('sticky-bundle-add-to-cart', StickyBundleAddToCart);
}